/**
 * Base de dados de sinais e sintomas (extraída do mockup Mãe Nutrida).
 *
 * - `PANELS` (sintomas por sistema) — aplicável a TODAS as categorias.
 * - `GEST_TRIMS` (sintomas gestacionais por trimestre) — apenas para Gestante.
 *
 * A inteligência clínica (hipóteses/nutrientes/exames) é gerada pelo
 * backend (assistant Nina Sinais & Sintomas).
 */

import { signs_symptom_trimester } from '@/entities/signs-symptom'

export interface SymptomItem {
  id: string
  name: string
}

export interface PanelDef {
  key: string
  icon: string
  title: string
  syms: SymptomItem[]
}

export interface TrimesterDef {
  key: signs_symptom_trimester
  label: string
  period: string
  syms: SymptomItem[]
}

export interface ExamRef {
  name: string
  why: string
}

export interface HypothesisDef {
  id: string
  name: string
  triggers: string[]
  threshold: number
  conduct: string
  exam: ExamRef
}

// ─────────────────────────────────────────────────────────────
// GESTACIONAIS — exibido como abas (1º/2º/3º trimestre) na UI
// quando categoria = GESTANTE.
// ─────────────────────────────────────────────────────────────

export const GEST_TRIMS: Record<signs_symptom_trimester, TrimesterDef> = {
  [signs_symptom_trimester.TRIM1]: {
    key: signs_symptom_trimester.TRIM1,
    label: '1º Trimestre',
    period: 'até 13ª semana',
    syms: [
      { id: 'g_nausea', name: 'Náuseas e vômitos' },
      { id: 'g_dorcab', name: 'Dor de cabeça' },
      { id: 'g_edema', name: 'Edema' },
      { id: 'g_fome', name: 'Fome excessiva' },
      { id: 'g_tontura', name: 'Tontura' },
      { id: 'g_doces', name: 'Vontade de comer doces' },
      { id: 'g_urinar', name: 'Dor/ardência ao urinar' },
      { id: 'g_resp', name: 'Respiração ofegante' }
    ]
  },
  [signs_symptom_trimester.TRIM2]: {
    key: signs_symptom_trimester.TRIM2,
    label: '2º Trimestre',
    period: '14ª a 27ª semana',
    syms: [
      { id: 'g_azia', name: 'Azia' },
      { id: 'g_refluxo', name: 'Refluxo' },
      { id: 'g_queimacao', name: 'Queimação' },
      { id: 'g_edema', name: 'Edema' },
      { id: 'g_fome', name: 'Fome excessiva' },
      { id: 'g_doces', name: 'Vontade de comer doces' },
      { id: 'g_resp', name: 'Respiração ofegante' },
      { id: 'g_estrias', name: 'Estrias' }
    ]
  },
  [signs_symptom_trimester.TRIM3]: {
    key: signs_symptom_trimester.TRIM3,
    label: '3º Trimestre',
    period: '28ª semana ao nascimento',
    syms: [
      { id: 'g_azia', name: 'Azia' },
      { id: 'g_refluxo', name: 'Refluxo' },
      { id: 'g_queimacao', name: 'Queimação' },
      { id: 'g_edema', name: 'Edema' },
      { id: 'g_fome', name: 'Fome excessiva' },
      { id: 'g_doces', name: 'Vontade de comer doces' },
      { id: 'g_resp', name: 'Respiração ofegante' },
      { id: 'g_cansaco', name: 'Cansaço excessivo' },
      { id: 'g_dorabs', name: 'Dor abdominal/baixo ventre' },
      { id: 'g_estrias', name: 'Estrias' }
    ]
  }
}

// ─────────────────────────────────────────────────────────────
// GERAIS
// ─────────────────────────────────────────────────────────────

export const HYPOTHESES: HypothesisDef[] = [
  {
    id: 'disbiose',
    name: 'Sinais de disbiose intestinal',
    triggers: ['c_fungos', 'c_caspa', 'b_mauhalito', 'b_lesbranq', 'b_leamarela', 'b_aftas', 'p_micose', 'tu_ardencia', 'e_fetidas', 'e_deslizam', 'e_comida', 'e_bolinha', 'n_panico', 'tu_itu'],
    threshold: 2,
    conduct: 'Investigar microbiota intestinal, enzimas digestivas e permeabilidade. Revisar dieta e histórico de antibióticos.',
    exam: { name: 'Microbiota (1º trimestre) · Ômega 3 index · PCR ultra-sensível', why: 'Sinais de desequilíbrio intestinal' }
  },
  {
    id: 'ri',
    name: 'Sinais de resistência à insulina',
    triggers: ['p_acantose', 's_sono', 'n_panico', 'n_sonolencia'],
    threshold: 1,
    conduct: 'Avaliar glicemia em jejum, insulina basal e HOMA-IR. Revisar carga glicêmica e fracionamento.',
    exam: { name: 'Glicemia jejum · Insulina jejum · Hemoglobina glicada · Glicemia pós-prandial · Insulina pós-prandial', why: 'Padrão sugestivo de resistência insulínica' }
  },
  {
    id: 'fungos',
    name: 'Sinais de síndrome fúngica (Candida)',
    triggers: ['c_fungos', 'c_caspa', 'b_lesbranq', 'b_paladar', 'p_micose', 'tu_ardencia', 'tu_itu', 'n_panico', 'e_fetidas'],
    threshold: 2,
    conduct: 'Restringir açúcar e carboidratos refinados. Investigar Candida sistêmica. Considerar probióticos e antifúngicos naturais.',
    exam: { name: 'Microbiota (1º trimestre) · PCR ultra-sensível · Albumina', why: 'Padrão fúngico sistêmico' }
  },
  {
    id: 'alergia',
    name: 'Sinais de sensibilidade / alergia alimentar',
    triggers: ['ol_olheiras', 'ol_bolsas', 'b_labinchado', 'b_aftas', 'p_alergias', 'p_rosacea', 'p_bolinhab', 'a_dor', 'n_enxaqueca', 'n_tontura', 's_insonia', 'r_asma'],
    threshold: 2,
    conduct: 'Investigar alérgenos principais (glúten, laticínios, soja, ovos). Considerar protocolo de exclusão por 4–6 semanas.',
    exam: { name: 'PCR ultra-sensível · Ômega 3 index · Albumina · Proteínas total', why: 'Múltiplos sinais de sensibilidade alimentar' }
  },
  {
    id: 'anemia',
    name: 'Sinais de anemia / carência Fe—B12—B9',
    triggers: ['n_sonolencia', 'n_tontura', 'a_fraqueza', 'r_taqui', 'r_respiracao', 'r_pressaobaixa', 'n_memoria'],
    threshold: 2,
    conduct: 'Solicitar hemograma completo com ferritina, B12 e ácido fólico. Investigar origem (ingesta, absorção, perda).',
    exam: { name: 'Hemograma completo · Ferritina · PCR ultra-sensível · Saturação de transferrina · Ret-He · Vitamina B12 · Folato eritrocitário · Homocisteína', why: 'Sintomas compatíveis com anemia' }
  },
  {
    id: 'tireoide',
    name: 'Sinais de disfunção tireoidiana',
    triggers: ['u_quebrad', 'u_descam', 'u_horizont', 'u_vertical', 's_insonia', 's_sonolodia', 'n_sonolencia', 'n_letargia', 'n_memoria', 'c_queda', 'a_fraqueza'],
    threshold: 2,
    conduct: 'Solicitar TSH, T3L, T4L e anticorpos. Avaliar iodo, selênio e zinco.',
    exam: { name: 'TSH · T4 livre · Anti-tireoglobulina · Anti-tireoperoxidase · Iodo urinário 24h · Selênio sérico', why: 'Sinais de disfunção tireoidiana' }
  },
  {
    id: 'defzn',
    name: 'Sinais de deficiência de zinco',
    triggers: ['c_queda', 'o_rachadura', 'b_paladar', 'p_cicatriz', 'u_manchasb', 'b_aftas', 'p_acne', 'b_lfissuras'],
    threshold: 2,
    conduct: 'Dosar zinco eritrocitário. Revisar ingestão e competições minerais (Cu, Ca). Verificar fitatos na dieta.',
    exam: { name: 'Zinco eritrocitário · Selênio sérico · Cobre sérico', why: 'Vários sinais clássicos de deficiência de Zn' }
  },
  {
    id: 'defmg',
    name: 'Sinais de deficiência de magnésio',
    triggers: ['b_bruxismo', 'a_caimbra', 'tu_noite', 'n_irrit', 'n_ansiedade', 'n_hiperativ', 'r_taqui', 'n_enxaqueca'],
    threshold: 2,
    conduct: 'Dosar Mg eritrocitário. Avaliar ingestão de folhas escuras, sementes e fatores de perda.',
    exam: { name: 'Magnésio sérico · Cálcio iônico · Cálcio total', why: 'Padrão compatível com deficiência de Mg' }
  },
  {
    id: 'hipocloridria',
    name: 'Sinais de hipocloridria',
    triggers: ['b_mauhalito', 'e_fetidas', 'e_comida', 'b_amargo', 'e_deslizam'],
    threshold: 2,
    conduct: 'Investigar H. pylori. Avaliar mastigação e ritmo alimentar.',
    exam: { name: 'TGO · TGP · GGT · Albumina · Proteínas total · Bilirrubinas', why: 'Sinais de hipocloridria e disfunção digestiva' }
  },
  {
    id: 'estresse_ox',
    name: 'Sinais de estresse oxidativo / detox comprometida',
    triggers: ['c_brancos', 'b_amargo', 'p_manchasroxas', 'p_varizes', 'n_tpm', 'b_leamarela', 'c_secos'],
    threshold: 2,
    conduct: 'Investigar função hepática e capacidade antioxidante. Revisar crucíferas e polifenóis.',
    exam: { name: 'TGO · TGP · GGT · Bilirrubinas · Albumina · 25(OH)D3 · PCR ultra-sensível · Homocisteína', why: 'Sinais de estresse oxidativo e detox comprometida' }
  }
]

export const NUTRIENTS: Record<string, string[]> = {
  'Zinco (Zn)': ['c_queda', 'o_rachadura', 'b_paladar', 'p_cicatriz', 'u_manchasb', 'b_aftas', 'p_acne', 'b_lfissuras', 'b_bruxismo'],
  'Magnésio (Mg)': ['b_bruxismo', 'a_caimbra', 'tu_noite', 'n_irrit', 'n_ansiedade', 'r_taqui', 'n_enxaqueca'],
  'Vitamina D': ['n_irrit', 'n_ansiedade', 'n_sonolencia', 'c_queda', 'a_fraqueza'],
  'Complexo B': ['b_fissuras', 'n_irrit', 'n_memoria', 'n_letargia', 'a_formig', 'c_secos'],
  'Ferro (Fe)': ['n_sonolencia', 'a_fraqueza', 'r_taqui', 'r_pressaobaixa', 'n_tontura'],
  'Vitamina A': ['p_seca', 'ol_secos', 'u_horizont', 'u_vertical', 'p_acne'],
  'Ômega-3': ['p_alergias', 'n_enxaqueca', 'a_dor', 'p_varizes', 'a_formig'],
  'Vitamina C': ['p_manchasroxas', 'p_cicatriz', 'b_gengiva'],
  'Biotina': ['c_queda', 'c_caspa', 'n_letargia'],
  'Cromo (Cr)': ['n_ansiedade']
}

export const PANELS: PanelDef[] = [
  {
    key: 'cabelos',
    icon: '💇',
    title: 'Cabelos',
    syms: [
      { id: 'c_caspa', name: 'Caspa' },
      { id: 'c_queda', name: 'Queda capilar' },
      { id: 'c_fungos', name: 'Fungos no couro' },
      { id: 'c_secos', name: 'Secos/quebradiços' },
      { id: 'c_brancos', name: 'Branqueamento precoce' },
      { id: 'c_alopecia', name: 'Alopécia' }
    ]
  },
  {
    key: 'ouvidos',
    icon: '👂',
    title: 'Ouvidos',
    syms: [
      { id: 'o_cera', name: 'Sensação de cera' },
      { id: 'o_coceira', name: 'Coceira' },
      { id: 'o_rachadura', name: 'Rachaduras atrás' },
      { id: 'o_zumbido', name: 'Zumbido' }
    ]
  },
  {
    key: 'olhos',
    icon: '👁️',
    title: 'Olhos',
    syms: [
      { id: 'ol_turva', name: 'Visão turva' },
      { id: 'ol_coceira', name: 'Coceira' },
      { id: 'ol_secos', name: 'Olhos secos' },
      { id: 'ol_olheiras', name: 'Olheiras' },
      { id: 'ol_bolsas', name: 'Bolsas/edema palpebral' }
    ]
  },
  {
    key: 'boca',
    icon: '🦷',
    title: 'Boca e Língua',
    syms: [
      { id: 'b_labinchado', name: 'Lábios inchados' },
      { id: 'b_fissuras', name: 'Fissuras labiais' },
      { id: 'b_herpes', name: 'Herpes labial' },
      { id: 'b_gengiva', name: 'Sangramento gengival' },
      { id: 'b_aftas', name: 'Aftas' },
      { id: 'b_mauhalito', name: 'Mau hálito' },
      { id: 'b_amargo', name: 'Gosto amargo' },
      { id: 'b_bruxismo', name: 'Bruxismo' },
      { id: 'b_lesbranq', name: 'Língua esbranquiçada' },
      { id: 'b_leamarela', name: 'Língua amarelada' },
      { id: 'b_lfissuras', name: 'Língua com fissuras' },
      { id: 'b_paladar', name: 'Perda do paladar' }
    ]
  },
  {
    key: 'resp',
    icon: '🫁',
    title: 'Respiratório/Circulação',
    syms: [
      { id: 'r_infeccoes', name: 'Infecções respiratórias' },
      { id: 'r_asma', name: 'Asma' },
      { id: 'r_taqui', name: 'Taquicardia' },
      { id: 'r_respiracao', name: 'Dific. de respirar' },
      { id: 'r_pressaoalta', name: 'Pressão alta' },
      { id: 'r_pressaobaixa', name: 'Pressão baixa' }
    ]
  },
  {
    key: 'pele',
    icon: '🩹',
    title: 'Pele',
    syms: [
      { id: 'p_seca', name: 'Pele seca' },
      { id: 'p_acantose', name: 'Acantose nigricans' },
      { id: 'p_alergias', name: 'Alergias' },
      { id: 'p_rosacea', name: 'Rosácea' },
      { id: 'p_cicatriz', name: 'Dific. cicatrização' },
      { id: 'p_manchasroxas', name: 'Manchas roxas fáceis' },
      { id: 'p_celulite', name: 'Celulite' },
      { id: 'p_varizes', name: 'Microvasos/varizes' },
      { id: 'p_bolinhab', name: 'Bolinhas no braço' },
      { id: 'p_acne', name: 'Acne' },
      { id: 'p_micose', name: 'Micose/frieira' },
      { id: 'p_tpm', name: 'TPM' }
    ]
  },
  {
    key: 'unhas',
    icon: '💅',
    title: 'Unhas',
    syms: [
      { id: 'u_horizont', name: 'Estrias horizontais' },
      { id: 'u_vertical', name: 'Estrias verticais' },
      { id: 'u_manchasb', name: 'Manchas brancas' },
      { id: 'u_micose', name: 'Micose' },
      { id: 'u_quebrad', name: 'Quebradiças' },
      { id: 'u_descam', name: 'Descamando' }
    ]
  },
  {
    key: 'urinario',
    icon: '🫧',
    title: 'Trato Urinário',
    syms: [
      { id: 'tu_turva', name: 'Urina turva' },
      { id: 'tu_dific', name: 'Dific. para urinar' },
      { id: 'tu_dor', name: 'Dor ao urinar' },
      { id: 'tu_itu', name: 'ITU recorrente' },
      { id: 'tu_noite', name: 'Acorda à noite para urinar' },
      { id: 'tu_ardencia', name: 'Ardência vaginal/anal' }
    ]
  },
  {
    key: 'nervoso',
    icon: '🧠',
    title: 'Sist. Nervoso e Humor',
    syms: [
      { id: 'n_irrit', name: 'Irritabilidade' },
      { id: 'n_ansiedade', name: 'Ansiedade' },
      { id: 'n_tpm', name: 'TPM' },
      { id: 'n_depressao', name: 'Depressão' },
      { id: 'n_letargia', name: 'Letargia/apatia' },
      { id: 'n_panico', name: 'Sínd. do pânico' },
      { id: 'n_hiperativ', name: 'Hiperatividade' },
      { id: 'n_sonolencia', name: 'Sonolência excessiva' },
      { id: 'n_concentracao', name: 'Dific. concentração' },
      { id: 'n_memoria', name: 'Redução de memória' },
      { id: 'n_tontura', name: 'Tontura' },
      { id: 'n_enxaqueca', name: 'Enxaqueca' }
    ]
  },
  {
    key: 'articulacoes',
    icon: '🦴',
    title: 'Articulações e Músculos',
    syms: [
      { id: 'a_dor', name: 'Dor muscular s/ exercício' },
      { id: 'a_caimbra', name: 'Câimbras' },
      { id: 'a_artrose', name: 'Artrose' },
      { id: 'a_artrite', name: 'Artrite' },
      { id: 'a_fraqueza', name: 'Fraqueza muscular' },
      { id: 'a_formig', name: 'Formigamento' }
    ]
  },
  {
    key: 'sono',
    icon: '😴',
    title: 'Sono',
    syms: [
      { id: 's_sono', name: 'Sono após refeições' },
      { id: 's_insonia', name: 'Insônia' },
      { id: 's_sonolodia', name: 'Sonolência ao longo do dia' }
    ]
  },
  {
    key: 'excrecao',
    icon: '💩',
    title: 'Excreção',
    syms: [
      { id: 'e_fetidas', name: 'Fezes muito fétidas' },
      { id: 'e_deslizam', name: 'Fezes que deslizam' },
      { id: 'e_bolinha', name: 'Fezes em bolinhas' },
      { id: 'e_comida', name: 'Comida nas fezes' },
      { id: 'e_finos', name: 'Fezes muito finos' }
    ]
  }
]

/**
 * Helper para localizar um sintoma pelo ID (usado em listagens consolidadas).
 * Procura primeiro nos GEST_TRIMS, depois nos PANELS.
 */
export function findSymptomName(id: string): string | undefined {
  for (const t of Object.values(GEST_TRIMS)) {
    const found = t.syms.find(s => s.id === id)
    if (found) return found.name
  }
  for (const p of PANELS) {
    const found = p.syms.find(s => s.id === id)
    if (found) return found.name
  }
  return undefined
}

/**
 * Constrói o mapa `generalSymptomsResolved` a partir dos IDs marcados na UI.
 *
 * Output: `{ [id]: { name: 'Queda capilar', system: 'Cabelos' } }`
 *
 * Necessário para que o backend envie NOMES LEGÍVEIS à IA (a Nina não conhece
 * IDs internos como `c_queda`).
 */
export function resolveSymptomMetadata(generalSymptoms: Record<string, boolean>): Record<string, { name: string; system?: string }> {
  const resolved: Record<string, { name: string; system?: string }> = {}
  for (const [id, checked] of Object.entries(generalSymptoms || {})) {
    if (!checked) continue
    for (const panel of PANELS) {
      const sym = panel.syms.find(s => s.id === id)
      if (sym) {
        resolved[id] = { name: sym.name, system: panel.title }
        break
      }
    }
    // IDs órfãos (não estão na lista atual) ficam sem entrada — o backend trata como legado.
  }
  return resolved
}

/**
 * Constrói o mapa `gestationalSymptomsResolved` a partir dos sintomas gestacionais
 * marcados por trimestre. Output:
 *
 *   { TRIM1: { [id]: { name } }, TRIM2: {...}, TRIM3: {...} }
 *
 * Apenas para categoria GESTANTE. O backend usa isso para contextualizar o
 * envio à IA por trimestre.
 */
import type { TrimesterChecks } from '@/entities/signs-symptom'

export function resolveGestationalSymptomMetadata(gestational: TrimesterChecks | null | undefined): Record<string, Record<string, { name: string }>> {
  if (!gestational) return {}
  const out: Record<string, Record<string, { name: string }>> = {}
  for (const [trimKey, syms] of Object.entries(gestational)) {
    const resolved: Record<string, { name: string }> = {}
    for (const [id, checked] of Object.entries(syms || {})) {
      if (!checked) continue
      const trim = GEST_TRIMS[trimKey as signs_symptom_trimester]
      const sym = trim?.syms.find(s => s.id === id)
      if (sym) resolved[id] = { name: sym.name }
    }
    if (Object.keys(resolved).length > 0) out[trimKey] = resolved
  }
  return out
}
